import { CookiesStorage } from '@ltvco/refresh-lib';
import {
  AccountPageV2,
  DeepSearchContainer,
  DeepSearchLayout,
  DeepSearchPage,
  DEEPSEARCH_ROUTES,
  MonitoredReportsPage,
  ReportLibraryLandingPage,
  UpgradePlanPage,
  type DeepSearchBrandConfig,
} from '@ltvco/refresh-lib/deepsearch';
import { Box, CircularProgress, Link, Text } from '@ltvco/refresh-lib/theme';
import {
  closeSnackbar,
  invalidateSession,
  useAccount,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { useFeatureIsOn, useGrowthBook } from '@ltvco/refresh-lib/vendors';
import { useCallback, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';

import { constants } from 'appConstants';
import { BaseNumerologyQueryKeys } from 'components/Astrology/interfaces';
import { ReportColumn } from 'components/DeepSearch/components/ReportColumn';
import { REPORT_NAV_CONFIG } from 'components/DeepSearch/components/ReportSideNav';
import {
  AI_DISCLAIMER_PARAGRAPHS,
  FEEDBACK_FORM_URL,
} from 'components/DeepSearch/constants';
import { SalesLeadModal } from 'components/SalesLeadModal';
import emptyListImage from 'images/img_empty_list.png';
import monitoredReportsEmptyImage from 'images/img_monitored_reports_empty.png';
import { NotFound } from 'routes';
import {
  LoadingReports,
  SearchAndFilterControls,
} from 'routes/Dashboard/RecentReports/components';
import { getPlanTitleAndDescription } from 'routes/Upgrade/plans';
import { UpgradePlanPage as UpgradePlanPageComponent } from 'routes/Upgrade/UpgradePlanPage';
import { goToLogin } from 'utils/goToLogin';
import { useLimitedPlanInfo } from 'utils/useLimitedPlanInfo';

// Both local-storage cleanup helpers are copied from App.tsx's onLogout
// handler (they're module-private there, not exported) -- kept in sync by
// hand since App.tsx no longer owns DeepSearch's logout flow.
function removeSensitiveLocalStorageData(): void {
  localStorage.removeItem('astrology_user');
  localStorage.removeItem(BaseNumerologyQueryKeys.lifepath);
  localStorage.removeItem(BaseNumerologyQueryKeys.personality);
}

function removeFamilyTreeLocalStorageData(): void {
  localStorage.removeItem('nodeRemovalApproved');
  localStorage.removeItem('reportIdsToFilterByType');
  localStorage.removeItem('bulkAddSubject');
  localStorage.removeItem('bulkAddSubjectReportInTree');
  localStorage.removeItem('bulkAddSubjectFamily');
  localStorage.removeItem('familyTreeRelativeToAdd');
  localStorage.removeItem('skipAutoTreeBuilder');
}

/**
 * The brand-side mount point for DeepSearch: builds the brandConfig the
 * shared DeepSearchContainer needs, and gates the whole feature behind the
 * `prop-3834` GrowthBook flag (replaces the old brand-local
 * DeepSearchContainer, which owned this same gating + routing before the
 * PROP-4198 migration moved the routing into refresh-lib).
 */
export const DeepSearchEntry = (): JSX.Element => {
  const gb = useGrowthBook();
  const isInDeepSearchVariation = useFeatureIsOn('prop-3834');
  const navigate = useNavigate();
  const {
    session: { isAuthenticated },
  } = useSession();
  const {
    data: account,
    isLoading: isAccountLoading,
    isError: isAccountError,
  } = useAccount(isAuthenticated);

  // Only navigate once the account is known to be loaded and non-noncompliant
  // -- while loading or on error, the noncompliant flag isn't known yet, so
  // don't let a click through as if it were.
  const onLogoClick = useCallback((): void => {
    if (isAccountLoading || isAccountError) {
      return;
    }
    if (account?.account?.user_info?.noncompliant) {
      return;
    }
    navigate('/dashboard');
  }, [account, isAccountLoading, isAccountError, navigate]);

  // Mirrors App.tsx's handleLogoutClick body. setIsAuthenticated(isUserLoggedIn())
  // is intentionally omitted -- goToLogin below is a hard browser navigation
  // (window.location.href / window.open(..., '_self')) that tears down this
  // SPA in every case except when already sitting on the login page, which
  // can't happen from inside DeepSearch.
  const onLogout = useCallback(async (): Promise<void> => {
    await invalidateSession();
    closeSnackbar();
    removeSensitiveLocalStorageData();
    removeFamilyTreeLocalStorageData();
    new CookiesStorage().setValue('partialLocView', 'false');
    localStorage.removeItem('upgradeLimitModalShown');
    goToLogin();
  }, []);

  // brandConfig must be referentially stable across renders (see
  // DeepSearchBrandConfig's own doc comment) -- memoized here, before the
  // gb.ready/feature-flag early returns below, so this hook always runs in
  // the same order regardless of which branch those returns take.
  const brandConfig: DeepSearchBrandConfig = useMemo(
    () => ({
      brandName: 'BeenVerified',
      logoSrc: constants.images.brandLogo,
      logoAlt: 'brand logo',
      onLogoClick,
      onLogout,
      notFound: <NotFound />,
      emptyListImageSrc: emptyListImage,
      monitoredReportsEmptyImageSrc: monitoredReportsEmptyImage,
      // Shipping directly rather than behind a flag -- PROP-4241, not an A/B test.
      voiceEnabled: true,
      legalContent: (
        <>
          <Text fontSize="0.813rem" color="text.secondary" textAlign="start">
            This chat feature is powered by artificial intelligence.
            BeenVerified is not responsible for any decisions, actions, or
            outcomes based on the information provided by the AI. The
            information provided or contained in any report and on our website
            is not necessarily 100% accurate, complete or up to date, nor a
            substitute for your own due diligence, especially concerning such
            sensitive items as criminal history, relatives, mortgages and liens.
            Our data comes from a wide variety of sources, but some
            municipalities and jurisdictions are slow to report and digitize
            their data, so we cannot guarantee or warrant full accuracy of ALL
            search results.
          </Text>
          <Text fontSize="0.813rem" color="text.secondary" textAlign="start">
            BeenVerified&apos;s mission is to give people easy and affordable
            access to public record information, but BeenVerified does not
            provide private investigator services or consumer reports, and is
            not a consumer reporting agency per the{' '}
            <Link
              href={constants.links.fcraLink}
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              fontWeight="bold"
            >
              Fair Credit Reporting Act
            </Link>{' '}
            . You may not use our site or service or the information provided to
            make decisions about employment, admission, consumer credit,
            insurance, tenant screening or any other purpose that would require
            FCRA compliance. For more information governing permitted and
            prohibited uses, please review our{' '}
            <Link
              href={constants.links.doDontsLink}
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              fontWeight="bold"
            >
              &quot;Do&apos;s &amp; Don&apos;ts&quot;
            </Link>{' '}
            and our{' '}
            <Link
              href={constants.links.tosLink}
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              fontWeight="bold"
            >
              Terms &amp; Conditions
            </Link>
            .
          </Text>
        </>
      ),
      aiDisclaimerParagraphs: AI_DISCLAIMER_PARAGRAPHS,
      feedbackFormUrl: FEEDBACK_FORM_URL,
      faqLink: constants.links.FaqLink,
      usePlanInfo: useLimitedPlanInfo,
      getPlanTitleAndDescription,
      planConfig: {
        validReportCountThreshold: constants.data.validReportCountThreshold,
        plansWithout5ReportBlocker: constants.data.plansWithout5ReportBlocker,
        blockUpgradeWindowThreshold: constants.data.blockUpgradeWindowThreshold,
      },
      reportNavConfig: REPORT_NAV_CONFIG,
      renderUpgradePlanContent: (contactUsHandler) => (
        <UpgradePlanPageComponent contactUsHandler={contactUsHandler} />
      ),
      renderSalesLeadModal: (props) => <SalesLeadModal {...props} />,
      renderSearchAndFilterControls: (props) => (
        <SearchAndFilterControls {...props} />
      ),
      renderLoadingReports: () => <LoadingReports />,
      renderReportColumn: (props) => <ReportColumn {...props} />,
    }),
    [onLogoClick, onLogout]
  );

  if (!gb?.ready) {
    return (
      <Box
        sx={{
          height: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isInDeepSearchVariation) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DeepSearchContainer brandConfig={brandConfig}>
      <Routes>
        <Route
          path="/*"
          element={
            <DeepSearchLayout>
              <Outlet />
            </DeepSearchLayout>
          }
        >
          <Route
            index
            element={<Navigate to={DEEPSEARCH_ROUTES.chat} replace />}
          />
          <Route path="chat" element={<DeepSearchPage />} />
          <Route
            path="chat/conversation/:contextId?"
            element={<DeepSearchPage />}
          />
          <Route
            path="chat/library"
            element={
              <ReportLibraryLandingPage basePath={DEEPSEARCH_ROUTES.chat} />
            }
          />
          <Route
            path="chat/library/monitored"
            element={<MonitoredReportsPage />}
          />
          <Route
            path="chat/library/list/:listCode"
            element={
              <ReportLibraryLandingPage basePath={DEEPSEARCH_ROUTES.chat} />
            }
          />
          <Route
            path="chat/library/:breadcrumbTitle"
            element={
              <ReportLibraryLandingPage basePath={DEEPSEARCH_ROUTES.chat} />
            }
          />
          <Route
            path="account"
            element={
              <AccountPageV2 upgradePath={DEEPSEARCH_ROUTES.upgradePlan} />
            }
          />
          <Route path="upgrade/plan" element={<UpgradePlanPage />} />
          <Route path="*" element={brandConfig.notFound} />
        </Route>
      </Routes>
    </DeepSearchContainer>
  );
};
